"use client"

import Link from "next/link"
import { useState } from "react"
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Zap,
  Shield,
  BarChart3,
  Globe,
  Layers,
  Server,
  Shuffle,
  Crown,
} from "lucide-react"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const orchestrationFeatures = [
  "Cascade failover",
  "FTD / Trusted routing",
  "Real-time analytics",
  "Load balancing",
  "Webhook notifications",
  "API access",
]

const managedFeatures = [
  "Everything in Orchestration, plus:",
  "22+ providers included",
  "No PSP applications needed",
  "Rolling reserve management",
  "Settlement scheduling",
  "Dispute management",
]

const hybridFeatures = [
  "Everything in Managed, plus:",
  "Mix BYO and managed providers",
  "Per-route fee optimization",
  "Volume discounts from $500K+/mo",
]

const comparisonRows: { feature: string; orch: string; managed: string; hybrid: string }[] = [
  { feature: "Payment providers", orch: "3 / 10 / Unlimited", managed: "22+ included", hybrid: "Unlimited" },
  { feature: "Cascade failover", orch: "Yes", managed: "Yes", hybrid: "Yes" },
  { feature: "FTD / Trusted segmentation", orch: "Yes", managed: "Yes", hybrid: "Yes" },
  { feature: "Real-time analytics", orch: "Yes", managed: "Yes", hybrid: "Yes" },
  { feature: "BYO credentials", orch: "Yes", managed: "No", hybrid: "Yes" },
  { feature: "Managed providers", orch: "No", managed: "Yes", hybrid: "Yes" },
  { feature: "Rolling reserve", orch: "N/A", managed: "Included", hybrid: "On managed routes" },
  { feature: "Settlement scheduling", orch: "Yes", managed: "Yes", hybrid: "Yes" },
  { feature: "Dispute management", orch: "Yes", managed: "Yes", hybrid: "Yes" },
  { feature: "Dedicated account manager", orch: "Enterprise only", managed: "$250K+/mo", hybrid: "$500K+/mo" },
  { feature: "SLA", orch: "99.9%", managed: "99.9%", hybrid: "99.9%" },
]

const faqs: { q: string; a: string }[] = [
  {
    q: "Can I switch between modes?",
    a: "Yes, you can upgrade or change modes at any time. Your existing routing rules and analytics history carry over seamlessly. Our team will handle the migration so there is zero downtime for your business.",
  },
  {
    q: "What PSPs do you support?",
    a: "We support 22+ providers including Mundo, Paytoro, Redsys, Nuvei, EUPaymentz, and many more. New integrations are added regularly. If you need a specific provider, reach out and we can prioritize it.",
  },
  {
    q: "How long does onboarding take?",
    a: "Orchestration: same day. Managed: 1-3 business days after KYB. Hybrid: 1-3 business days. We provide dedicated onboarding support to get you processing as quickly as possible.",
  },
  {
    q: "Is there a minimum volume?",
    a: "No minimum for Orchestration. Managed PSP is best suited for merchants processing $10K+/mo. There are no long-term contracts \u2014 you can scale up or down as your business needs change.",
  },
  {
    q: "What about chargebacks?",
    a: "We provide dispute management tools and chargeback alerts across all plans. This includes real-time notifications, evidence submission workflows, and analytics to help you reduce dispute ratios over time.",
  },
]

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-border dark:border-white/[0.06] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-base font-medium text-foreground/90 group-hover:text-foreground transition-colors pr-4">
          {q}
        </span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
        )}
      </button>
      {open && (
        <p className="pb-5 text-sm leading-relaxed text-muted-foreground -mt-1">{a}</p>
      )}
    </div>
  )
}

function CellBadge({ value }: { value: string }) {
  const isYes = value === "Yes"
  const isNo = value === "No" || value === "N/A"
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm ${
        isYes ? "text-emerald-400" : isNo ? "text-gray-600" : "text-muted-foreground"
      }`}
    >
      {isYes && <Check className="w-3.5 h-3.5" />}
      {value}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* ── Background ─────────────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-cyan-500/[0.07] via-blue-500/[0.04] to-transparent rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-gradient-to-t from-purple-500/[0.05] to-transparent rounded-full blur-[80px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
      </div>

      {/* ── Nav ────────────────────────────────────────────────── */}
      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 py-5">
        <Link href="/" className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/favicon.svg" alt="FluxPay" className="w-9 h-9" />
          <span className="font-semibold tracking-wide uppercase text-xl hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          <span className="font-semibold tracking-wide uppercase text-xl inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
        </Link>

        <div className="flex items-center gap-3">
          <Link
            href="/pricing"
            className="hidden md:inline-flex items-center px-4 py-2.5 text-sm font-medium text-foreground transition-colors"
          >
            Pricing
          </Link>
          <Link
            href="/docs"
            className="hidden md:inline-flex items-center px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Docs
          </Link>
          <Link
            href="/auth/signin"
            className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border dark:border-white/[0.08] hover:border-border dark:hover:border-white/[0.15] rounded-xl transition-all"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="relative z-10 pt-16 pb-12 px-6 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
          <span className="text-foreground">Simple, Transparent&nbsp;</span>
          <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
            Pricing
          </span>
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Choose the mode that fits your business. Scale as you grow.
        </p>
      </section>

      {/* ── Pricing Cards ──────────────────────────────────────── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-20">
        <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* ─ Orchestration ─ */}
          <div className="relative flex flex-col rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08] hover:border-cyan-500/30 transition-colors duration-300 overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-cyan-400 to-cyan-600" />
            <div className="p-7 flex flex-col flex-1">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Orchestration</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                For merchants with existing PSP relationships
              </p>

              {/* Sub-tiers */}
              <div className="space-y-4 mb-8">
                <div className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">Starter</span>
                    <span className="text-lg font-bold text-foreground">$99<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                  </div>
                  <p className="text-xs text-muted-foreground">$0.05/txn &middot; Up to 3 providers</p>
                </div>

                <div className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">Growth</span>
                    <span className="text-lg font-bold text-foreground">$299<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                  </div>
                  <p className="text-xs text-muted-foreground">$0.03/txn &middot; Up to 10 providers</p>
                </div>

                <div className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium text-muted-foreground">Enterprise</span>
                    <span className="text-lg font-bold text-foreground">Custom</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Custom pricing &middot; Unlimited providers</p>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {orchestrationFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href="/signup"
                className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 shadow-lg shadow-cyan-500/20 transition-all duration-200"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ─ Managed PSP ─ */}
          <div className="relative flex flex-col rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08] hover:border-cyan-500/30 transition-colors duration-300 overflow-hidden ring-1 ring-cyan-500/20">
            <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-cyan-500" />

            {/* Badge */}
            <div className="absolute top-5 right-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-cyan-500/10 text-cyan-500 dark:text-cyan-300 border border-cyan-500/20">
                <Crown className="w-3 h-3" />
                Most Popular
              </span>
            </div>

            <div className="p-7 flex flex-col flex-1">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Server className="w-5 h-5 text-cyan-500 dark:text-cyan-300" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Managed PSP</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Start processing immediately &mdash; we provide the PSPs
              </p>

              {/* Pricing table */}
              <div className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] overflow-hidden mb-8">
                <div className="grid grid-cols-[1fr,auto] text-xs">
                  <div className="px-4 py-2.5 border-b border-border dark:border-white/[0.06] text-muted-foreground font-medium">
                    Monthly Volume
                  </div>
                  <div className="px-4 py-2.5 border-b border-border dark:border-white/[0.06] text-muted-foreground font-medium text-right">
                    Rate
                  </div>

                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-muted-foreground">
                    &lt; $50K
                  </div>
                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-foreground font-semibold text-right">
                    5.5% + $0.25
                  </div>

                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-muted-foreground">
                    $50K &ndash; $250K
                  </div>
                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-foreground font-semibold text-right">
                    4.5% + $0.25
                  </div>

                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-muted-foreground">
                    $250K &ndash; $1M
                  </div>
                  <div className="px-4 py-3 border-b border-border dark:border-white/[0.04] text-foreground font-semibold text-right">
                    3.5% + $0.20
                  </div>

                  <div className="px-4 py-3 text-muted-foreground">&gt; $1M</div>
                  <div className="px-4 py-3 text-foreground font-semibold text-right">Custom</div>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {managedFeatures.map((f, i) => (
                  <li
                    key={f}
                    className={`flex items-start gap-2.5 text-sm ${
                      i === 0 ? "text-cyan-500 dark:text-cyan-300 font-medium" : "text-muted-foreground"
                    }`}
                  >
                    {i === 0 ? (
                      <Zap className="w-4 h-4 text-cyan-500 dark:text-cyan-300 mt-0.5 shrink-0" />
                    ) : (
                      <Check className="w-4 h-4 text-cyan-500 dark:text-cyan-300 mt-0.5 shrink-0" />
                    )}
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href="/signup"
                className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 shadow-lg shadow-blue-500/20 transition-all duration-200"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ─ Hybrid ─ */}
          <div className="relative flex flex-col rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08] hover:border-purple-500/30 transition-colors duration-300 overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-purple-400 to-purple-600" />
            <div className="p-7 flex flex-col flex-1">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Shuffle className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Hybrid</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Use your providers + ours for full coverage
              </p>

              {/* Pricing */}
              <div className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] p-4 mb-8 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Base fee</span>
                  <span className="text-lg font-bold text-foreground">$199<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                </div>
                <div className="h-px bg-border dark:bg-white/[0.06]" />
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">BYO routes</span>
                  <span className="text-sm font-semibold text-foreground">$0.04<span className="text-xs font-normal text-muted-foreground">/txn</span></span>
                </div>
                <div className="h-px bg-border dark:bg-white/[0.06]" />
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Managed routes</span>
                  <span className="text-sm font-semibold text-foreground">MDR per volume tier</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {hybridFeatures.map((f, i) => (
                  <li
                    key={f}
                    className={`flex items-start gap-2.5 text-sm ${
                      i === 0 ? "text-purple-400 font-medium" : "text-muted-foreground"
                    }`}
                  >
                    {i === 0 ? (
                      <Zap className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                    ) : (
                      <Check className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                    )}
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href="/signup"
                className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 shadow-lg shadow-purple-500/20 transition-all duration-200"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature Comparison ─────────────────────────────────── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Feature Comparison</h2>
            <p className="mt-3 text-muted-foreground">See how each mode stacks up.</p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.08] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border dark:border-white/[0.08]">
                  <th className="text-left px-6 py-4 text-muted-foreground font-medium">Feature</th>
                  <th className="text-center px-6 py-4">
                    <span className="text-cyan-400 font-semibold">Orchestration</span>
                  </th>
                  <th className="text-center px-6 py-4">
                    <span className="text-cyan-500 dark:text-cyan-300 font-semibold">Managed PSP</span>
                  </th>
                  <th className="text-center px-6 py-4">
                    <span className="text-purple-400 font-semibold">Hybrid</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-border dark:border-white/[0.04] ${
                      i % 2 === 0 ? "bg-gray-50 dark:bg-white/[0.01]" : ""
                    }`}
                  >
                    <td className="px-6 py-4 text-muted-foreground font-medium">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      <CellBadge value={row.orch} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CellBadge value={row.managed} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <CellBadge value={row.hybrid} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile comparison cards */}
          <div className="md:hidden space-y-3">
            {comparisonRows.map((row) => (
              <div
                key={row.feature}
                className="rounded-xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] p-4"
              >
                <p className="text-sm font-medium text-foreground mb-3">{row.feature}</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="block text-cyan-400/70 mb-1">Orchestration</span>
                    <CellBadge value={row.orch} />
                  </div>
                  <div>
                    <span className="block text-cyan-500/80 dark:text-cyan-300/80 mb-1">Managed</span>
                    <CellBadge value={row.managed} />
                  </div>
                  <div>
                    <span className="block text-purple-400/70 mb-1">Hybrid</span>
                    <CellBadge value={row.hybrid} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-24">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Frequently Asked Questions</h2>
            <p className="mt-3 text-muted-foreground">Everything you need to know before getting started.</p>
          </div>

          <div className="rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08] px-6 sm:px-8">
            {faqs.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ─────────────────────────────────────────── */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-16 pb-20">
        <div className="mx-auto max-w-4xl rounded-2xl bg-gradient-to-br from-gray-100 dark:from-white/[0.04] to-gray-50 dark:to-white/[0.01] border border-border dark:border-white/[0.08] p-10 sm:p-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
            Ready to get started?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Create your account in minutes and start processing payments today. No credit card required for Orchestration mode.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/signup"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 shadow-lg shadow-cyan-500/20 transition-all duration-200"
            >
              Create Free Account
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="/docs"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground border border-border dark:border-white/[0.08] hover:border-border dark:hover:border-white/[0.15] transition-all duration-200"
            >
              Read the Docs
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="relative z-10 px-6 sm:px-10 lg:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border dark:border-white/[0.04]">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-600">
          <span>&copy; {new Date().getFullYear()} FluxPay Financial Services</span>
          <span className="hidden sm:inline">&middot;</span>
          <span>FINTRAC Registered MSB</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
          {[
            { label: "Terms", href: "/terms" },
            { label: "Privacy", href: "/privacy" },
            { label: "AML Policy", href: "/aml-policy" },
            { label: "Refund Policy", href: "/refund-policy" },
            { label: "Docs", href: "/docs" },
          ].map((link, i) => (
            <Link
              key={i}
              href={link.href}
              className="text-muted-foreground hover:text-muted-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </footer>
    </div>
  )
}
